import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api'
import { Currency } from '@sequencehq/api/commonEnums'
import {
  Text,
  GreyGrey10,
  GreyGrey30,
  Primary13Regular,
  Primary16SemiBold,
  Flex
} from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'
import invariant from 'tiny-invariant'
import { floatSafeAdd, toMoney } from '@sequencehq/utils'
import { Skeleton } from '@sequencehq/core-components'
import { useSelectedCurrency } from 'RevenueRecognition/view/utils/useSelectedCurrency'
import { useSelectedDateRange } from 'RevenueRecognition/view/utils/useSelectedDateRange'
import { useSearchParams } from 'react-router-dom'
import { useDefaultCurrency } from 'SettingsV2/index'

const useRevRecDashboardStatsCard = (props: {
  currency?: Currency | null
  dateRange?: [Date, Date]
  customerIds?: string[]
  productIds?: string[]
  invoiceIds?: string[]
  creditNoteIds?: string[]
}):
  | {
      isLoading: true
      data: null
    }
  | {
      isLoading: false
      data: {
        totalDeferred: string
        totalRecognized: string
        startDate: Date
        endDate: Date
      }
    } => {
  const defaultCurrency = useDefaultCurrency(!!props.currency)

  const currency = props.currency ?? defaultCurrency
  const chartsDataResponse = useQuery(
    dashboardv99990101Client.getRevRecChartsData,
    {
      // Have to put a default here otherwise typescript will be upset
      // we are checking for currency above and in the enabled check below
      currency: currency ?? 'GBP',
      from: props.dateRange?.[0]
        ? format(props.dateRange[0], 'yyyy-MM-dd')
        : undefined,
      to: props.dateRange?.[1]
        ? format(props.dateRange[1], 'yyyy-MM-dd')
        : undefined,
      ...(props.customerIds ? { customerId: props.customerIds.join(',') } : {}),
      ...(props.productIds ? { productId: props.productIds.join(',') } : {}),
      ...(props.invoiceIds ? { invoiceId: props.invoiceIds.join(',') } : {}),
      ...(props.creditNoteIds
        ? { creditNoteId: props.creditNoteIds.join(',') }
        : {})
    },
    {
      enabled: !!currency,
      staleTime: 60000,
      select: data => {
        if (!data) {
          return
        }

        const deferredRevenueByMonth = data.period.buckets.map(
          bucket =>
            bucket.recognizedAndDeferred.ledgerAccountTotals.find(
              i => i.name === 'DEFERRED_REVENUE'
            )?.total || 0
        )

        const recognizedRevenueByMonth = data.period.buckets.map(
          bucket =>
            bucket.recognizedAndDeferred.ledgerAccountTotals.find(
              i => i.name === 'RECOGNIZED_REVENUE'
            )?.total || 0
        )

        // Handle floating point precision errors
        // If the value is essentially zero (less than 1 cent), treat it as zero
        const totalDeferred = deferredRevenueByMonth.reduce(floatSafeAdd, 0)
        const totalRecognized = recognizedRevenueByMonth.reduce(floatSafeAdd, 0)

        const normalizeZero = (value: number) => {
          // If absolute value is less than 0.01 (1 cent), treat as zero
          return Math.abs(value) < 0.01 ? 0 : value
        }

        return {
          totalDeferred: normalizeZero(totalDeferred),
          totalRecognized: normalizeZero(totalRecognized),
          startDate: new Date(data.period.startDate),
          endDate: new Date(data.period.endDate)
        }
      }
    }
  )

  if (chartsDataResponse.isError) {
    throw new Error('Failed to load deferred revenue data')
  }

  if (chartsDataResponse.isPending) {
    return {
      isLoading: true,
      data: null
    }
  }

  invariant(
    chartsDataResponse.data,
    'Revenue recognition stats data should be defined, if loaded'
  )

  return {
    data: {
      totalDeferred: toMoney({
        value: `${chartsDataResponse.data.totalDeferred}`,
        currency: currency ?? 'GBP'
      }),
      totalRecognized: toMoney({
        value: `${chartsDataResponse.data.totalRecognized}`,
        currency: currency ?? 'GBP'
      }),
      startDate: chartsDataResponse.data.startDate,
      endDate: chartsDataResponse.data.endDate
    },
    isLoading: false
  }
}

export const RevRecDashboardStatsCard = (props: { hasResults: boolean }) => {
  const { currency } = useSelectedCurrency()
  const { dateRange } = useSelectedDateRange()

  const [searchParams] = useSearchParams()
  const customerIds = searchParams.get('customerId')
  const productIds = searchParams.get('productId')
  const invoiceIds = searchParams.get('invoiceId')
  const creditNoteIds = searchParams.get('creditNoteId')

  const { isLoading, data } = useRevRecDashboardStatsCard({
    currency,
    dateRange,
    customerIds: customerIds ? customerIds.split(',') : undefined,
    productIds: productIds ? productIds.split(',') : undefined,
    invoiceIds: invoiceIds ? invoiceIds.split(',') : undefined,
    creditNoteIds: creditNoteIds ? creditNoteIds.split(',') : undefined
  })

  if (isLoading) {
    return (
      <Flex p="16px" borderBottom="1px" borderColor={GreyGrey30}>
        <Skeleton height="96px" width="100%" borderRadius={8} />
      </Flex>
    )
  }

  if (!props.hasResults) {
    return null
  }

  return (
    <Flex p="16px" borderBottom="1px" borderColor={GreyGrey30}>
      <Flex
        height="96px"
        width="100%"
        borderRadius={8}
        backgroundColor={GreyGrey10}
        flexDirection="column"
        alignContent="center"
        alignItems="center"
        justifyContent="center"
        gap="8px"
        data-testid="revenueRecognition.journals.summary"
      >
        <Text {...Primary16SemiBold}>
          {format(data?.startDate, 'd MMM yyyy')} -{' '}
          {format(data?.endDate, 'd MMM yyyy')}
        </Text>
        <Flex alignItems="center" gap="4px" {...Primary13Regular}>
          <Text>Recognized:</Text>
          <Text fontWeight="semibold">{data?.totalRecognized}</Text>
          <Text marginX="4px">·</Text>
          <Text>Deferred:</Text>
          <Text
            fontWeight="semibold"
            data-testid="revenueRecognition.journals.summary.deferredValue"
          >
            {data?.totalDeferred}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}
